// src/layouts/dashboards/CourierDashboard.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "layouts/dashboards/DashboardLayout";
import DashboardNavbar from "../../examples/Navbars/DashboardNavbar";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LocationSearchingIcon from "@mui/icons-material/LocationSearching";
import MapIcon from "@mui/icons-material/Map";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";
import RouteIcon from "@mui/icons-material/Route";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import {
  approveTaskOverride,
  assignTaskDriver,
  denyTaskOverride,
  getBackendCapabilities,
  getTasks,
  listDrivers,
  request,
} from "../../api/client";
import {
  driverNameOf,
  formatAbsoluteTime,
  getCurrentTaskForDriver,
  getDeadlineIso,
  getPendingOverride,
  getTaskLastUpdateIso,
  getLocationIssues,
  getTaskRisk,
  hasAssignedDriver,
  hasLocationIssue,
  isEmergencyTask,
  isReassignmentRequired,
  isStaleDriver,
  locationIssueSummary,
  locationStatusInfo,
  nextDestination,
  reassignmentMessage,
  relativeTime,
  STOP_FIELDS,
  staleSeverity,
  taskIdentity,
} from "../../utils/dispatchOps";
import { isTerminalTaskStatus, normalizeTaskStatus } from "../../constants/operations";

const STATUS_COLOR = {
  unscheduled: "default",
  assigned: "info",
  "en route pickup": "warning",
  "en route": "warning",
  "picked up": "warning",
  "at pickup": "warning",
  "in transit": "warning",
  "en route delivery": "warning",
  arrived: "secondary",
  delivered: "success",
  completed: "success",
  canceled: "error",
  failed: "error",
  exception: "error",
};

const FILTERS = {
  ALL: "all",
  UNASSIGNED: "unassigned",
  RISK: "risk",
  OVERRIDE: "override",
  LOCATION: "location",
  STALE_DRIVER: "stale-driver",
  EMERGENCY: "emergency",
};

function statusColor(status) {
  return STATUS_COLOR[normalizeTaskStatus(status)] || "default";
}

function taskTypeLabel(type) {
  if (!type || type === "standard") return null;
  const map = {
    pickup: { label: "PICKUP", color: "primary" },
    delivery: { label: "DELIVERY", color: "secondary" },
  };
  return map[String(type).toLowerCase()] || null;
}

function queueIssueDescriptor(task, staleDriverLevels, nowMs) {
  const driver = driverNameOf(task);
  const risk = getTaskRisk(task, nowMs);
  const pendingOverride = getPendingOverride(task);
  const staleLevel = driver ? staleDriverLevels.get(String(driver).trim().toLowerCase()) : null;
  const staleDriver = staleLevel === "warning" || staleLevel === "critical";

  if (isEmergencyTask(task)) {
    return { priority: 1, label: "EMERGENCY", color: "error", detail: "Immediate dispatch attention required" };
  }
  if (isReassignmentRequired(task)) {
    return {
      priority: 2,
      label: "DRIVER REMOVED",
      color: "error",
      detail: reassignmentMessage(task),
    };
  }
  if (!hasAssignedDriver(task) && ["Late", "Critical", "High"].includes(risk.level)) {
    return { priority: 3, label: "UNASSIGNED URGENT", color: "error", detail: risk.label };
  }
  if (pendingOverride) {
    return {
      priority: 4,
      label: `${pendingOverride.type.toUpperCase()} OVERRIDE`,
      color: "secondary",
      detail: pendingOverride.reason || "Dispatcher approval required",
    };
  }
  if (hasLocationIssue(task)) {
    return { priority: 5, label: "LOCATION BLOCKED", color: "warning", detail: locationIssueSummary(task) };
  }
  if (["Late", "Critical", "High"].includes(risk.level)) {
    return { priority: 6, label: risk.level === "Late" ? "LATE" : "AT RISK", color: "warning", detail: risk.label };
  }
  if (staleDriver) {
    return {
      priority: 7,
      label: staleLevel === "critical" ? "STALE DRIVER — CRITICAL" : "STALE DRIVER",
      color: staleLevel === "critical" ? "error" : "warning",
      detail: "Assigned driver has not updated recently",
    };
  }
  return null;
}

function filterCardProps(key, activeFilter) {
  return {
    role: "button",
    tabIndex: 0,
    onKeyDown: (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        activeFilter.set(key);
      }
    },
    onClick: () => activeFilter.set(key),
    sx: { cursor: "pointer" },
  };
}

function FilterStatCard({ label, value, caption, filterKey, activeFilter, color = "primary" }) {
  const selected = activeFilter.value === filterKey;
  return (
    <Card
      elevation={0}
      {...filterCardProps(filterKey, activeFilter)}
      sx={{
        cursor: "pointer",
        borderRadius: 3,
        height: "100%",
        border: (theme) =>
          `1px solid ${selected ? theme.palette[color]?.main || theme.palette.primary.main : theme.palette.divider}`,
        boxShadow: selected ? "0 0 0 2px rgba(25,118,210,0.08)" : "none",
        bgcolor: selected ? `${color}.50` : "background.paper",
      }}
    >
      <CardContent sx={{ pb: "16px !important" }}>
        <Typography variant="caption" color={selected ? `${color}.main` : "text.secondary"} sx={{ fontWeight: 700 }}>
          {label}
        </Typography>
        <Typography variant="h4" sx={{ mt: 0.75, fontWeight: 700 }} color={selected ? `${color}.main` : "text.primary"}>
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
      </CardContent>
    </Card>
  );
}

function LocationFixDialog({ task, onClose, onFixed }) {
  // The pickup and the delivery each have their own stored point, status and
  // geocoder suggestion. Open on the stop that has the problem.
  const issues = getLocationIssues(task);
  const [stop,     setStop]     = useState(issues[0]?.stop || "dropoff");
  const [lat,      setLat]      = useState("");
  const [lng,      setLng]      = useState("");
  const [saving,   setSaving]   = useState(false);
  const [regeocing,setRegeocing]= useState(false);
  const [error,    setError]    = useState("");
  const [success,  setSuccess]  = useState("");

  const f = STOP_FIELDS[stop];

  // Reset when the task (or the chosen stop's stored point) changes
  useEffect(() => {
    setStop(getLocationIssues(task)[0]?.stop || "dropoff");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.id]);
  useEffect(() => {
    setLat(String(task?.[f.lat] ?? ""));
    setLng(String(task?.[f.lng] ?? ""));
    setError("");
    setSuccess("");
  }, [task?.id, stop, task?.[f.lat], task?.[f.lng]]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!task) return null;

  const status    = task[f.status];
  const candidate = task[f.candidate];
  const address   = task[f.address];
  const locInfo   = locationStatusInfo(status);
  const hasPoint  = task[f.lat] != null && task[f.lng] != null;
  const mapsLink  = (la, ln) => `https://www.google.com/maps?q=${la},${ln}`;

  // Apply a change to the task held by the dashboard (status/candidate follow the stop).
  const fixed = (patch) => onFixed({ ...task, ...patch });

  const handleSave = async () => {
    const parsedLat = parseFloat(lat);
    const parsedLng = parseFloat(lng);
    if (isNaN(parsedLat) || isNaN(parsedLng)) {
      setError("Enter valid decimal coordinates (e.g. 40.7128, -74.0060).");
      return;
    }
    if (parsedLat < -90 || parsedLat > 90) {
      setError("Latitude must be between -90 and 90.");
      return;
    }
    if (parsedLng < -180 || parsedLng > 180) {
      setError("Longitude must be between -180 and 180.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const patch = {
        [f.lat]: parsedLat,
        [f.lng]: parsedLng,
        [f.status]: "manual_override",
        [f.candidate]: null,
      };
      const res = await request(`/tasks/${task.id}`, { method: "PUT", body: patch });
      if (res.ok) {
        setSuccess(`${f.label} coordinates saved — (${parsedLat.toFixed(5)}, ${parsedLng.toFixed(5)})`);
        fixed(patch);
      } else {
        const d = await res.json().catch(() => ({}));
        setError(d?.detail ?? `Save failed (HTTP ${res.status}).`);
      }
    } catch {
      setError("Server unreachable.");
    } finally {
      setSaving(false);
    }
  };

  const handleRegeocode = async () => {
    setRegeocing(true);
    setError("");
    setSuccess("");
    try {
      const res = await request(`/tasks/${task.id}/geocode?stop=${stop}`, { method: "POST" });
      const d   = await res.json().catch(() => ({}));
      if (res.ok) {
        const newStatus = d.status ?? d.locationStatus;
        if (newStatus === "geocoded") {
          const la = d.lat ?? d.dropoffLat;
          const ln = d.lng ?? d.dropoffLng;
          setLat(String(la));
          setLng(String(ln));
          setSuccess(`Geocoded — (${Number(la).toFixed(5)}, ${Number(ln).toFixed(5)})`);
          fixed({ [f.lat]: la, [f.lng]: ln, [f.status]: "geocoded", [f.candidate]: null });
        } else if (newStatus === "geocode_approximate" && d.candidate) {
          setError(
            `Only an approximate point (${d.candidate.precision}) was found for "${address}". ` +
            "It was not saved. Check it on the map, then use it or enter exact coordinates below."
          );
          fixed({ [f.status]: "geocode_approximate", [f.candidate]: d.candidate });
        } else {
          setError(`Geocoding failed for "${address}". Enter coordinates manually below.`);
          fixed({ [f.status]: newStatus || "geocode_failed", [f.candidate]: null });
        }
      } else {
        setError(d?.detail ?? `Re-geocode failed (HTTP ${res.status}).`);
      }
    } catch {
      setError("Server unreachable.");
    } finally {
      setRegeocing(false);
    }
  };

  const useCandidate = () => {
    if (!candidate) return;
    setLat(String(candidate.lat));
    setLng(String(candidate.lng));
    setError("");
    setSuccess("Suggested point loaded below — verify it on the map, then Save Coordinates.");
  };

  return (
    <Dialog
      open={!!task}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 0.5, pr: 6 }}>
        <Typography fontWeight={700} fontSize={16}>
          Fix Location — {(task.id || "").substring(0, 8)}…
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {task.clientName || task.dropoffAddress || "—"}
        </Typography>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{ position: "absolute", right: 12, top: 14 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* Which stop */}
        <Box mb={2} display="flex" gap={1}>
          {Object.keys(STOP_FIELDS).map((key) => {
            const hasIssue = issues.some((i) => i.stop === key);
            return (
              <Button
                key={key}
                size="small"
                variant={stop === key ? "contained" : "outlined"}
                color={hasIssue ? "warning" : "primary"}
                onClick={() => setStop(key)}
              >
                {STOP_FIELDS[key].label}{hasIssue ? " ⚠" : ""}
              </Button>
            );
          })}
        </Box>

        {/* Address + current status */}
        <Box mb={2}>
          <Typography variant="caption" color="text.secondary" display="block" mb={0.25}>
            {stop === "pickup" ? "PICKUP ADDRESS" : "DELIVERY ADDRESS"}
          </Typography>
          <Typography variant="body2" fontWeight={500}>
            {address || "(none)"}
          </Typography>
        </Box>

        <Box mb={2} display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
          <Chip
            size="small"
            label={status ? locInfo.label : hasPoint ? "SET" : locInfo.label}
            color={status || !hasPoint ? locInfo.chipColor : "success"}
            variant="filled"
          />
          {hasPoint ? (
            <Typography variant="caption" color="text.secondary">
              ({Number(task[f.lat]).toFixed(5)}, {Number(task[f.lng]).toFixed(5)}){" "}
              <a href={mapsLink(task[f.lat], task[f.lng])} target="_blank" rel="noreferrer">view on map</a>
            </Typography>
          ) : (
            <Typography variant="caption" color="error.main" fontWeight={700}>
              Coordinates not set
            </Typography>
          )}
        </Box>

        {locInfo.message && (status || !hasPoint) && (
          <Alert severity={locInfo.severity || "warning"} sx={{ mb: 2 }}>
            {locInfo.message}
          </Alert>
        )}

        {candidate && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={useCandidate}>
                Use point
              </Button>
            }
          >
            Geocoder suggestion ({candidate.precision}, {candidate.source}): ({Number(candidate.lat).toFixed(5)}, {Number(candidate.lng).toFixed(5)}){" "}
            <a href={mapsLink(candidate.lat, candidate.lng)} target="_blank" rel="noreferrer">view on map</a>
          </Alert>
        )}

        {error   && <Alert severity="error"   sx={{ mb: 1.5 }} onClose={() => setError("")}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 1.5 }}>{success}</Alert>}

        <Divider sx={{ mb: 2 }} />

        {/* Option 1 — re-geocode */}
        <Typography variant="subtitle2" fontWeight={700} mb={0.75}>
          Option 1 — Re-geocode from address
        </Typography>
        <Button
          variant="outlined"
          fullWidth
          disabled={regeocing || !address}
          onClick={handleRegeocode}
          startIcon={
            regeocing
              ? <CircularProgress size={16} />
              : <LocationSearchingIcon fontSize="small" />
          }
          sx={{ mb: 2.5, justifyContent: "flex-start" }}
        >
          {regeocing ? "Geocoding…" : `Re-geocode "${address || "(no address)"}"`}
        </Button>

        {/* Option 2 — manual */}
        <Typography variant="subtitle2" fontWeight={700} mb={0.75}>
          Option 2 — Enter coordinates manually
        </Typography>
        <Box display="flex" gap={2} mb={0.5}>
          <TextField
            label="Latitude"
            value={lat}
            onChange={(e) => setLat(e.target.value)}
            size="small"
            fullWidth
            placeholder="40.7128"
            type="number"
            inputProps={{ step: "any" }}
          />
          <TextField
            label="Longitude"
            value={lng}
            onChange={(e) => setLng(e.target.value)}
            size="small"
            fullWidth
            placeholder="-74.0060"
            type="number"
            inputProps={{ step: "any" }}
          />
        </Box>
        <Typography variant="caption" color="text.secondary">
          Decimal degrees. Tip: right-click the exact door/dock in Google Maps and copy the coordinates.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button variant="outlined" color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <MyLocationIcon fontSize="small" />}
        >
          Save Coordinates
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function OverrideDecisionDialog({ task, onClose, onDecision }) {
  const pending = getPendingOverride(task);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    setNote("");
    setSaving("");
    setError("");
  }, [task?.id, pending?.type]);

  if (!task || !pending) return null;

  const handleDecision = async (decision) => {
    setSaving(decision);
    setError("");
    try {
      await onDecision(task, pending, decision, note.trim());
    } catch (err) {
      setError(err?.message || "Server unreachable.");
    } finally {
      setSaving("");
    }
  };

  return (
    <Dialog open={!!task} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 0.5, pr: 6 }}>
        <Typography fontWeight={700} fontSize={16}>
          Override Request — {pending.type === "arrival" ? "Arrival" : pending.type === "pickup" ? "Pickup" : "Delivery"}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {task.clientName || task.dropoffAddress || "—"}
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ position: "absolute", right: 12, top: 14 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}
        <Box mb={2}>
          <Chip
            size="small"
            color="warning"
            label={`${pending.type.toUpperCase()} OVERRIDE REQUESTED`}
            sx={{ fontWeight: 700 }}
          />
        </Box>
        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
          Driver request
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          {pending.reason || "No reason provided."}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          Requested by {pending.requested_by_email || pending.requested_by || "driver"}
          {pending.requested_at ? ` • ${formatAbsoluteTime(pending.requested_at)}` : ""}
        </Typography>
        <TextField
          label="Dispatcher note (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          fullWidth
          multiline
          minRows={3}
          inputProps={{ maxLength: 240 }}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button variant="outlined" color="inherit" onClick={onClose} disabled={!!saving}>
          Close
        </Button>
        <Button
          variant="outlined"
          color="error"
          onClick={() => handleDecision("deny")}
          disabled={!!saving}
          startIcon={saving === "deny" ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Deny Override
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleDecision("approve")}
          disabled={!!saving}
          startIcon={saving === "approve" ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Approve Override
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AssignDriverDialog({ task, drivers, onClose, onAssign }) {
  const [driverId, setDriverId] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setDriverId("");
    setSaving(false);
    setError("");
  }, [task?.id]);

  if (!task) return null;

  const availableDrivers = drivers.filter((driver) => {
    const status = normalizeTaskStatus(driver.operational_status);
    return status === "available" || status === "assigned";
  });

  const handleAssign = async () => {
    if (!driverId) {
      setError("Select a driver before assigning this task.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onAssign(task, driverId);
    } catch (err) {
      setError(err?.message || "Server unreachable.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!task} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 0.5, pr: 6 }}>
        <Typography fontWeight={700} fontSize={16}>
          Assign Driver
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {taskIdentity(task).title}
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ position: "absolute", right: 12, top: 14 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}
        <TextField
          select
          fullWidth
          label="Driver"
          value={driverId}
          onChange={(e) => setDriverId(e.target.value)}
          helperText="Command-center assignment. Task List remains the full record system."
        >
          {availableDrivers.map((driver) => (
            <MenuItem key={driver.id} value={driver.id}>
              {driver.name} • {driver.operational_status || "Available"}
            </MenuItem>
          ))}
        </TextField>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button variant="outlined" color="inherit" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleAssign} disabled={saving} startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <PersonAddAlt1Icon fontSize="small" />}>
          Assign Driver
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function CourierDashboard() {
  const navigate = useNavigate();
  const hasLoadedRef = useRef(false);
  const [tasks, setTasks] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [capabilities, setCapabilities] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState({ open: false, severity: "success", message: "" });
  const [lastSync, setLastSync] = useState(null);
  const [nowTick, setNowTick] = useState(Date.now());
  const [fixTask, setFixTask] = useState(null);
  const [overrideTask, setOverrideTask] = useState(null);
  const [assignTask, setAssignTask] = useState(null);
  const [activeFilter, setActiveFilter] = useState(FILTERS.ALL);

  const pushNotice = useCallback((severity, message) => {
    setNotice({ open: true, severity, message });
  }, []);

  const closeNotice = useCallback(() => {
    setNotice((prev) => ({ ...prev, open: false }));
  }, []);

  const load = useCallback(async ({ background = false } = {}) => {
    if (background || hasLoadedRef.current) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const [tasksRes, driversRes, caps] = await Promise.all([getTasks(), listDrivers(), getBackendCapabilities().catch(() => null)]);
      if (!tasksRes.ok)   throw new Error(`Tasks fetch failed: ${tasksRes.status}`);
      if (!driversRes.ok) throw new Error(`Drivers fetch failed: ${driversRes.status}`);
      const tasksData   = await tasksRes.json();
      const driversData = await driversRes.json();
      setTasks(Array.isArray(tasksData)   ? tasksData   : []);
      setDrivers(Array.isArray(driversData) ? driversData : []);
      setCapabilities(caps);
      setLastSync(new Date().toISOString());
      hasLoadedRef.current = true;
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
      setNowTick(Date.now());
    }
  }, []);

  useEffect(() => {
    load();
    const pollId = setInterval(() => load({ background: true }), 15_000);
    const tickId = setInterval(() => setNowTick(Date.now()), 10_000);
    return () => {
      clearInterval(pollId);
      clearInterval(tickId);
    };
  }, [load]);

  const activeTasks = useMemo(
    () => tasks.filter((task) => !isTerminalTaskStatus(task.status)),
    [tasks]
  );

  const staleDriverLevels = useMemo(() => {
    const levels = new Map();
    drivers.forEach((driver) => {
      const name = String(driver.name || "").trim().toLowerCase();
      if (!name) return;
      levels.set(name, staleSeverity(driver.updated_at, nowTick));
    });
    return levels;
  }, [drivers, nowTick]);

  const queueEntries = useMemo(() => {
    return activeTasks
      .map((task) => {
        const descriptor = queueIssueDescriptor(task, staleDriverLevels, nowTick);
        if (!descriptor) return null;
        return {
          task,
          descriptor,
          risk: getTaskRisk(task, nowTick),
          pendingOverride: getPendingOverride(task),
          lastUpdate: getTaskLastUpdateIso(task),
        };
      })
      .filter(Boolean)
      .sort((a, b) => {
        if (a.descriptor.priority !== b.descriptor.priority) {
          return a.descriptor.priority - b.descriptor.priority;
        }
        const aDeadline = new Date(getDeadlineIso(a.task) || getTaskLastUpdateIso(a.task) || 0).getTime();
        const bDeadline = new Date(getDeadlineIso(b.task) || getTaskLastUpdateIso(b.task) || 0).getTime();
        if (a.risk.sortWeight !== b.risk.sortWeight) return a.risk.sortWeight - b.risk.sortWeight;
        return aDeadline - bDeadline;
      });
  }, [activeTasks, staleDriverLevels, nowTick]);

  const queueByFilter = useMemo(() => {
    switch (activeFilter) {
      case FILTERS.UNASSIGNED:
        return queueEntries.filter(({ task }) => !hasAssignedDriver(task));
      case FILTERS.RISK:
        return queueEntries.filter(({ risk }) => ["Late", "Critical", "High"].includes(risk.level));
      case FILTERS.OVERRIDE:
        return queueEntries.filter(({ pendingOverride }) => !!pendingOverride);
      case FILTERS.LOCATION:
        return queueEntries.filter(({ task }) => hasLocationIssue(task));
      case FILTERS.STALE_DRIVER:
        return queueEntries.filter(({ task }) => {
          const level = staleDriverLevels.get(String(driverNameOf(task)).trim().toLowerCase());
          return level === "warning" || level === "critical";
        });
      case FILTERS.EMERGENCY:
        return queueEntries.filter(({ task }) => isEmergencyTask(task));
      default:
        return queueEntries;
    }
  }, [activeFilter, queueEntries, staleDriverLevels]);

  const kpis = useMemo(() => {
    const unassignedActive = activeTasks.filter((task) => !hasAssignedDriver(task));
    const atRiskLate = activeTasks.filter((task) => ["Late", "Critical", "High"].includes(getTaskRisk(task, nowTick).level));
    const pendingOverrideTasks = activeTasks.filter((task) => !!getPendingOverride(task));
    const locationIssues = activeTasks.filter(hasLocationIssue);
    const staleDrivers = drivers.filter((driver) => isStaleDriver(driver.updated_at, 5, nowTick));
    const emergencyJobs = activeTasks.filter(isEmergencyTask);
    return { unassignedActive, atRiskLate, pendingOverrideTasks, locationIssues, staleDrivers, emergencyJobs };
  }, [activeTasks, drivers, nowTick]);

  const snapshotRows = useMemo(() => {
    return drivers
      .map((driver) => {
        const currentTask = getCurrentTaskForDriver(driver, activeTasks);
        if (!currentTask) return null;
        return {
          id: driver.id,
          driver,
          currentTask,
        };
      })
      .filter(Boolean)
      .sort((a, b) => new Date(b.driver.updated_at || 0).getTime() - new Date(a.driver.updated_at || 0).getTime());
  }, [activeTasks, drivers]);

  const openTaskControl = (task) => {
    try {
      window.sessionStorage.setItem("ci_focus_task_id", task.id);
    } catch {
      // ignore storage failures
    }
    navigate("/applications/task-list");
  };

  const openMap = (task) => {
    try {
      window.sessionStorage.setItem("ci_live_map_focus_task_id", task.id);
      const matchedDriver = drivers.find(
        (driver) => String(driver.name || "").trim().toLowerCase() === String(driverNameOf(task) || "").trim().toLowerCase()
      );
      if (matchedDriver?.id) {
        window.sessionStorage.setItem("ci_live_map_focus_driver_id", matchedDriver.id);
      }
    } catch {
      // ignore storage failures
    }
    navigate("/live-map");
  };

  const openRoutePlanner = (task) => {
    try {
      window.sessionStorage.setItem("ci_route_planner_focus_task_id", task.id);
    } catch {
      // ignore storage failures
    }
    navigate("/applications/route-planner");
  };

  const handleFixed = (updated) => {
    setTasks((prev) => prev.map((t) => (t.id === updated.id ? { ...t, ...updated } : t)));
  };

  const handleOverrideDecision = async (task, pending, decision, note) => {
    const previousTask = task;
    const nextState = `${pending.type}_override_${decision === "approve" ? "approved" : "denied"}`;
    const optimisticTask = {
      ...task,
      overrides: {
        ...(task.overrides || {}),
        [pending.type]: {
          ...(task.overrides?.[pending.type] || {}),
          ...pending,
          state: nextState,
          note,
          reviewed_at: new Date().toISOString(),
        },
      },
      [`${pending.type}OverrideState`]: nextState,
      updatedAt: new Date().toISOString(),
    };

    setTasks((prev) => prev.map((row) => (row.id === task.id ? optimisticTask : row)));
    setOverrideTask(null);
    pushNotice("info", `${pending.type} override ${decision} in progress…`);

    try {
      const res = decision === "approve"
        ? await approveTaskOverride(task.id, pending.type, note)
        : await denyTaskOverride(task.id, pending.type, note);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.detail || `${decision} failed (HTTP ${res.status}).`);
      }
      const updatedTask = data?.task || optimisticTask;
      setTasks((prev) => prev.map((row) => (row.id === task.id ? { ...row, ...updatedTask } : row)));
      pushNotice("success", `${pending.type} override ${decision === "approve" ? "approved" : "denied"}.`);
    } catch (err) {
      setTasks((prev) => prev.map((row) => (row.id === task.id ? previousTask : row)));
      pushNotice("error", err?.message || "Override decision failed.");
      throw err;
    }
  };

  const handleAssignDriver = async (task, driverId) => {
    const selectedDriver = drivers.find((driver) => driver.id === driverId);
    if (!selectedDriver) {
      throw new Error("Selected driver was not found.");
    }

    const nowIso = new Date().toISOString();
    const previousTask = task;
    const previousDrivers = drivers;
    const optimisticTask = {
      ...task,
      status: "Assigned",
      assigned_driver_id: driverId,
      driver: selectedDriver.name,
      assignedDriver: selectedDriver.name,
      updatedAt: nowIso,
    };

    setTasks((prev) => prev.map((row) => (row.id === task.id ? optimisticTask : row)));
    setDrivers((prev) =>
      prev.map((driver) =>
        driver.id === driverId
          ? {
              ...driver,
              operational_status: "Assigned",
              current_task_id: task.id,
              updated_at: nowIso,
            }
          : driver
      )
    );
    setAssignTask(null);
    pushNotice("info", `Assigning ${selectedDriver.name}…`);

    try {
      const res = await assignTaskDriver(task.id, driverId);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.detail || `Assignment failed (HTTP ${res.status}).`);
      }
      const confirmedName = data?.driver_name || selectedDriver.name;
      setTasks((prev) =>
        prev.map((row) =>
          row.id === task.id
            ? {
                ...row,
                status: "Assigned",
                assigned_driver_id: driverId,
                driver: confirmedName,
                assignedDriver: confirmedName,
                updatedAt: nowIso,
              }
            : row
        )
      );
      pushNotice("success", `${confirmedName} assigned.`);
    } catch (err) {
      setTasks((prev) => prev.map((row) => (row.id === task.id ? previousTask : row)));
      setDrivers(previousDrivers);
      pushNotice("error", err?.message || "Driver assignment failed.");
      throw err;
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box px={2} py={3}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Courier Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Dispatch command center for exceptions, approvals, and immediate action.
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Last system refresh {lastSync ? `${relativeTime(lastSync, nowTick)} • ${formatAbsoluteTime(lastSync)}` : "pending"}
              {refreshing ? " • updating…" : ""}
            </Typography>
          </Box>
          <Button
            size="small"
            startIcon={loading || refreshing ? <CircularProgress size={14} /> : <RefreshIcon fontSize="small" />}
            onClick={() => load({ background: true })}
            disabled={loading || refreshing}
          >
            {loading || refreshing ? "Refreshing…" : "Refresh"}
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} lg={2}>
            <FilterStatCard
              label="Unassigned Active"
              value={kpis.unassignedActive.length}
              caption="Needs assignment"
              filterKey={FILTERS.UNASSIGNED}
              activeFilter={{ value: activeFilter, set: setActiveFilter }}
              color="error"
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={2}>
            <FilterStatCard
              label="At Risk / Late"
              value={kpis.atRiskLate.length}
              caption="Deadline pressure"
              filterKey={FILTERS.RISK}
              activeFilter={{ value: activeFilter, set: setActiveFilter }}
              color="warning"
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={2}>
            <FilterStatCard
              label="Pending Overrides"
              value={kpis.pendingOverrideTasks.length}
              caption="Approval required"
              filterKey={FILTERS.OVERRIDE}
              activeFilter={{ value: activeFilter, set: setActiveFilter }}
              color="secondary"
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={2}>
            <FilterStatCard
              label="Location Issues"
              value={kpis.locationIssues.length}
              caption="Completion blocked"
              filterKey={FILTERS.LOCATION}
              activeFilter={{ value: activeFilter, set: setActiveFilter }}
              color="warning"
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={2}>
            <FilterStatCard
              label="Stale Drivers"
              value={kpis.staleDrivers.length}
              caption="Needs check-in"
              filterKey={FILTERS.STALE_DRIVER}
              activeFilter={{ value: activeFilter, set: setActiveFilter }}
              color="warning"
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={2}>
            <FilterStatCard
              label="Emergency Jobs"
              value={kpis.emergencyJobs.length}
              caption="Priority response"
              filterKey={FILTERS.EMERGENCY}
              activeFilter={{ value: activeFilter, set: setActiveFilter }}
              color="error"
            />
          </Grid>
        </Grid>

        <Card elevation={0} sx={{ borderRadius: 3, border: (theme) => `1px solid ${theme.palette.divider}`, mb: 2 }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={2}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Action Queue
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Prioritized by operational urgency. Only tasks requiring action are shown.
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Last queue evaluation {formatAbsoluteTime(new Date(nowTick).toISOString())}
                </Typography>
              </Box>
              {activeFilter !== FILTERS.ALL && (
                <Button size="small" onClick={() => setActiveFilter(FILTERS.ALL)}>
                  Clear Filter
                </Button>
              )}
            </Box>

            {loading && queueByFilter.length === 0 ? (
              <Box py={4} textAlign="center">
                <CircularProgress size={28} />
              </Box>
            ) : queueByFilter.length === 0 ? (
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                All operations normal — no active exceptions. Last evaluated {formatAbsoluteTime(new Date(nowTick).toISOString())}.
              </Alert>
            ) : (
              <Stack spacing={1.5}>
                {queueByFilter.map(({ task, descriptor, risk, pendingOverride, lastUpdate }) => {
                  const identity = taskIdentity(task);
                  const typeChip = taskTypeLabel(task.taskType);
                  const locIssue = hasLocationIssue(task);
                  const lastUpdatedLabel = relativeTime(lastUpdate, nowTick);
                  const deadlineIso = task.deliveryDeadline || task.milestones?.deliveryDeadline || null;
                  const deadlineLabel = deadlineIso
                    ? new Date(deadlineIso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
                    : "No deadline";

                  return (
                    <Box
                      key={task.id}
                      sx={{
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                        borderRadius: 2.5,
                        p: 2,
                        bgcolor: descriptor.priority <= 2 ? "error.50" : descriptor.priority <= 4 ? "warning.50" : "background.paper",
                      }}
                    >
                      <Box display="flex" justifyContent="space-between" gap={2} flexWrap="wrap">
                        <Box minWidth={0}>
                          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={0.5}>
                            <Chip size="small" color={descriptor.color} label={descriptor.label} sx={{ fontWeight: 700 }} />
                            {typeChip && <Chip size="small" variant="outlined" color={typeChip.color} label={typeChip.label} />}
                            <Chip size="small" variant="outlined" color={statusColor(task.status)} label={task.status || "Unscheduled"} />
                          </Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            {identity.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {identity.route}
                          </Typography>
                        </Box>
                        <Box textAlign={{ xs: "left", md: "right" }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Deadline / aging
                          </Typography>
                          <Typography variant="body2" fontWeight={700} color={risk.level === "Late" ? "error.main" : "text.primary"}>
                            {deadlineLabel}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {risk.label}
                          </Typography>
                        </Box>
                      </Box>

                      <Grid container spacing={1.5} sx={{ mt: 0.5, mb: 1 }}>
                        <Grid item xs={12} md={3}>
                          <Typography variant="caption" color="text.secondary">Driver</Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {driverNameOf(task) || "UNASSIGNED"}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Typography variant="caption" color="text.secondary">Issue flag</Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {descriptor.detail}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Typography variant="caption" color="text.secondary">Next destination</Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {nextDestination(task)}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Typography variant="caption" color="text.secondary">Last update</Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {lastUpdatedLabel}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatAbsoluteTime(lastUpdate)}
                          </Typography>
                        </Grid>
                      </Grid>

                      <Box display="flex" gap={1} flexWrap="wrap">
                        {!hasAssignedDriver(task) && (
                          <Button size="small" variant="contained" startIcon={<PersonAddAlt1Icon fontSize="small" />} onClick={() => setAssignTask(task)}>
                            Assign
                          </Button>
                        )}
                        <Button size="small" variant="outlined" startIcon={<OpenInNewIcon fontSize="small" />} onClick={() => openTaskControl(task)}>
                          Open Task
                        </Button>
                        <Button size="small" variant="outlined" startIcon={<MapIcon fontSize="small" />} onClick={() => openMap(task)}>
                          View Map
                        </Button>
                        {pendingOverride && capabilities?.routes?.taskOverrideApprove !== false && (
                          <Button size="small" variant="outlined" color="secondary" onClick={() => setOverrideTask(task)}>
                            Approve Override
                          </Button>
                        )}
                        {(locIssue || hasAssignedDriver(task)) && (
                          <Button size="small" variant="outlined" startIcon={<RouteIcon fontSize="small" />} onClick={() => openRoutePlanner(task)}>
                            Re-route
                          </Button>
                        )}
                        {locIssue && (
                          <Button size="small" variant="outlined" color="warning" startIcon={<LocationSearchingIcon fontSize="small" />} onClick={() => setFixTask(task)}>
                            Fix Location
                          </Button>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </CardContent>
        </Card>

        <Card elevation={0} sx={{ borderRadius: 3, border: (theme) => `1px solid ${theme.palette.divider}` }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={2}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Active Operations Snapshot
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Live operational view of drivers currently carrying work.
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Fleet refresh {lastSync ? `${relativeTime(lastSync, nowTick)} • ${formatAbsoluteTime(lastSync)}` : "pending"}
                </Typography>
              </Box>
            </Box>

            {snapshotRows.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                No active driver-task pairings right now. Use Driver Status to review fleet readiness and Task List for assignment records.
              </Alert>
            ) : (
              <Stack spacing={1} divider={<Divider flexItem />}>
                {snapshotRows.map(({ driver, currentTask }) => (
                  <Box key={driver.id} display="grid" gridTemplateColumns={{ xs: "1fr", md: "1.1fr 1.1fr 0.8fr 1fr auto" }} gap={2} alignItems="center">
                    <Box>
                      <Typography variant="caption" color="text.secondary">Driver</Typography>
                      <Typography variant="body2" fontWeight={700}>{driver.name}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Current task</Typography>
                      <Typography variant="body2" fontWeight={700}>{taskIdentity(currentTask).title}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Status</Typography>
                      <Typography variant="body2" fontWeight={700}>{driver.operational_status || currentTask.status || "—"}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Next destination</Typography>
                      <Typography variant="body2" fontWeight={700}>{nextDestination(currentTask)}</Typography>
                    </Box>
                    <Box textAlign={{ xs: "left", md: "right" }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Last sync
                      </Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {relativeTime(driver.updated_at, nowTick)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {formatAbsoluteTime(driver.updated_at)}
                      </Typography>
                      <Box display="flex" gap={1} justifyContent={{ xs: "flex-start", md: "flex-end" }} mt={0.75}>
                        <Button size="small" onClick={() => openMap(currentTask)}>Map</Button>
                        <Button size="small" onClick={() => openTaskControl(currentTask)}>Task</Button>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Stack>
            )}
          </CardContent>
        </Card>
      </Box>

      <LocationFixDialog
        task={fixTask}
        onClose={() => setFixTask(null)}
        onFixed={(updated) => {
          handleFixed(updated);
          setFixTask(null);
        }}
      />
      <OverrideDecisionDialog
        task={overrideTask}
        onClose={() => setOverrideTask(null)}
        onDecision={handleOverrideDecision}
      />
      <AssignDriverDialog
        task={assignTask}
        drivers={drivers}
        onClose={() => setAssignTask(null)}
        onAssign={handleAssignDriver}
      />
      <Snackbar
        open={notice.open}
        autoHideDuration={4000}
        onClose={closeNotice}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={notice.severity} variant="filled" onClose={closeNotice} sx={{ width: "100%" }}>
          {notice.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}
